<script setup lang="ts">
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { format as formatDate } from "date-fns";
import { BookmarkPlus, Loader2 } from "@lucide/vue";

import { useModalStore } from "@/stores/modals/modals.store";
import { useProjectRevisionsStore } from "../stores/projectRevisions.store";
import type { ProjectRevision } from "../types/projectRevision.types";

// Confirmation for switching the project to another revision. Unlike previewing, this changes what
// the whole team works on: the revision being left keeps everything done while it was current, and
// this one becomes the working state for everyone. Nothing is created and nothing is lost, so the
// dialog's job is to say what moves — not to warn.
const props = defineProps<{
  projectId: string;
  revision: ProjectRevision;
}>();

const toast = useToast();
const { closeByName } = useModalStore();
const revisionsStore = useProjectRevisionsStore();

const isSwitching = ref(false);

const savedAt = computed(() => formatDate(new Date(props.revision.createdAt), "MMM d, yyyy 'at' HH:mm"));

/** The revision being left — named, because "your work is kept" is only reassuring if it says where. */
const currentRevisionName = computed(() => revisionsStore.activeMetaFor(props.projectId)?.name ?? null);

const cancel = () => closeByName("MakeRevisionCurrentModal", { action: "cancel" });

const confirm = async () => {
  if (isSwitching.value) return;
  isSwitching.value = true;
  try {
    await revisionsStore.activateRevision(props.projectId, props.revision.id);
    toast.success(`Now working from “${props.revision.name}”.`);
    closeByName("MakeRevisionCurrentModal", { action: "switched" });
  } catch (e) {
    Sentry.captureException(e);
    toast.error("Could not switch to this revision. Please try again.");
  } finally {
    isSwitching.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Continue from “{{ revision.name }}”?</h2>

    <p class="t-small text-type-muted">
      This moves the working state of the whole project to this save point. Everyone on the project follows immediately, and from
      then on changes are made — and visible — on top of it.
    </p>

    <!-- What you're switching to -->
    <div class="rounded-8 p-px-12 gap-px-4 flex flex-col bg-slate-100 ring-1 ring-slate-200">
      <p class="t-small-bold text-type-default">{{ revision.name }}</p>
      <p class="t-caption text-type-muted">{{ savedAt }} · {{ revision.createdBy.name }}</p>
    </div>

    <!-- The two halves of the move. No new revisions, which is the thing worth being clear about. -->
    <ul class="gap-px-8 t-small text-type-muted flex list-disc flex-col pl-5">
      <li v-if="currentRevisionName">
        Everything done while <span class="t-small-bold text-type-default">“{{ currentRevisionName }}”</span> was current is saved
        to it, so it is there when you come back.
      </li>
      <li>
        Materials, project information and the transport and waste mappings become the ones
        <span class="t-small-bold text-type-default">“{{ revision.name }}”</span> is holding.
      </li>
    </ul>

    <p class="t-caption text-type-muted">
      No revision is created and none is deleted. You can switch between save points as often as you like — each one keeps the
      work done while it was current.
    </p>

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isSwitching"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isSwitching },
        ]"
        data-cy="make-current-cancel"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="isSwitching"
        :class="[
          btnBase,
          !isSwitching
            ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': isSwitching },
        ]"
        data-cy="make-current-confirm"
        @click="confirm">
        <Loader2
          v-if="isSwitching"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        <BookmarkPlus
          v-else
          class="icon-sm"
          :stroke-width="1.5" />
        {{ isSwitching ? "Switching…" : "Continue from this revision" }}
      </button>
    </div>
  </div>
</template>
